import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { getLeads } from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import type { EntityListOfLeadDto, LeadStatus } from "Infrastructure/Api/Api";

const LEAD_LIST_QUERY_KEY = "LEAD_LIST";

export const leadListKeys = {
  all: [LEAD_LIST_QUERY_KEY] as const,
  filtered: (
    registeredAsRole?: string,
    leadStatus?: LeadStatus,
    invitedAsRole?: string,
  ) =>
    [
      ...leadListKeys.all,
      { registeredAsRole, leadStatus, invitedAsRole },
    ] as const,
  paginated: (
    limit: number,
    registeredAsRole?: string,
    leadStatus?: LeadStatus,
    invitedAsRole?: string,
  ) =>
    [
      ...leadListKeys.filtered(registeredAsRole, leadStatus, invitedAsRole),
      { limit },
    ] as const,
};

export type LeadListQueryParams = {
  registeredAsRole?: string;
  leadStatus?: LeadStatus;
  invitedAsRole?: string;
  limit?: number;
  offset?: number;
};

type PageParams = {
  offset: number;
  limit: number;
  registeredAsRole?: string;
  leadStatus?: LeadStatus;
  invitedAsRole?: string;
};

export const useInfiniteLeadListQuery = ({
  registeredAsRole,
  leadStatus,
  invitedAsRole,
  limit = 10,
}: LeadListQueryParams) =>
  useInfiniteQuery<
    EntityListOfLeadDto,
    Error,
    InfiniteData<EntityListOfLeadDto>,
    ReturnType<typeof leadListKeys.paginated>,
    PageParams
  >({
    queryKey: leadListKeys.paginated(
      limit,
      registeredAsRole,
      leadStatus,
      invitedAsRole,
    ),
    queryFn: processResponseWithApiProblemDetails(
      ({ pageParam: { offset, limit } }) =>
        getLeads(offset, limit, leadStatus, invitedAsRole, registeredAsRole),
    ),
    initialPageParam: {
      offset: 0,
      limit,
      leadStatus,
      invitedAsRole,
      registeredAsRole,
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) {
        return undefined;
      }

      const isFinalPage =
        lastPage.offset + lastPage.limit >= lastPage.totalCount;

      if (isFinalPage) {
        return undefined;
      }

      return {
        offset: lastPage.offset + lastPage.limit,
        limit,
        leadStatus,
        invitedAsRole,
        registeredAsRole,
      };
    },
  });
